import type { FC, ReactNode } from 'react'
import type { StreamItem } from '@/types'

import { useRef } from 'react'
import { ArrowUpRight, Play, Youtube } from 'lucide-react'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import SectionHeading from '@/components/landing/section-heading'
import MediaImage from '@/components/landing/media-image'
import StreamStatusBadge from '@/components/landing/stream-status'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import PatternArt from '@/components/landing/decor/pattern-art'
import { STREAM_COPY } from '@/constants/content'
import { siteConfig } from '@/constants/site'

interface StreamSectionProps {
    streams: Array<StreamItem>
}

interface StreamCardProps {
    stream: StreamItem
}

const CARD_CLASS = 'group relative flex h-full flex-col overflow-hidden rounded-[20px] bg-mist/85 shadow-[0_34px_70px_-42px_rgba(29,42,51,0.5)] ring-1 ring-ink/10 backdrop-blur-sm transition-shadow duration-700 hover:shadow-[0_40px_80px_-38px_rgba(29,42,51,0.6)]'

// Shown until the item has a thumbnail of its own (YouTube items always do).
const ThumbnailFallback: FC<{ seed: string }> = ({ seed }): ReactNode => (
    <div className='absolute inset-0'>
        <PatternArt seed={seed} className='absolute inset-0 h-full w-full' />
        <span className='absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.36em] text-ink/55'>AMAMA NIA</span>
    </div>
)

// One YouTube item. Titles that come from the CMS stay inline-editable; YouTube titles do not.
const StreamCard: FC<StreamCardProps> = ({ stream }): ReactNode => {
    const fromCms = stream.source === 'cms'

    const body = (
        <>
            <div className='relative aspect-video overflow-hidden bg-ink/5'>
                {stream.thumbnail
                    ? (
                        <MediaImage
                            src={stream.thumbnail}
                            alt={stream.thumbnailAlt ?? stream.title}
                            sizes='(min-width: 1024px) 31vw, 80vw'
                            className='object-cover transition-transform duration-[1400ms] ease-[var(--ease-cinema)] group-hover:scale-[1.06]'
                        />
                    )
                    : <ThumbnailFallback seed={stream.id} />}
                <div className='absolute inset-0 bg-gradient-to-t from-ink/35 via-ink/0 to-ink/0' />
                <span className='absolute left-3 top-3'>
                    <StreamStatusBadge status={stream.status} />
                </span>
                {stream.url && (
                    <span className='absolute bottom-3 right-3 flex size-10 items-center justify-center rounded-full bg-mist/85 text-ink shadow-lg backdrop-blur transition-transform duration-700 ease-[var(--ease-cinema)] group-hover:scale-110'>
                        <Play className='ml-0.5 size-4' />
                    </span>
                )}
            </div>

            <div className='flex flex-1 flex-col gap-3 p-5 sm:p-6'>
                {stream.dateLabel && (
                    <time dateTime={stream.dateTime ?? undefined} className='font-mono text-[11px] tracking-[0.16em] text-ink-soft'>
                        {stream.dateLabel}
                    </time>
                )}
                <h3
                    data-mdlfy-cms={fromCms ? `streams:${stream.id}:title` : undefined}
                    className='line-clamp-2 text-base leading-[1.75] tracking-[0.04em]'
                >
                    {stream.title}
                </h3>
                {stream.url && (
                    <span className='mt-auto inline-flex items-center gap-1.5 pt-1 font-mono text-[10px] tracking-[0.22em] text-ink-soft transition-colors duration-500 group-hover:text-ink'>
                        <Youtube className='size-4' />
                        {STREAM_COPY.watch}
                        <ArrowUpRight className='size-3.5' />
                    </span>
                )}
            </div>
        </>
    )

    if (stream.url) {
        return (
            <a
                href={stream.url}
                target='_blank'
                rel='noopener noreferrer'
                data-stream-card
                className={`${CARD_CLASS} focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold`}
            >
                {body}
                <span className='sr-only'>（YouTube が新しいタブで開きます）</span>
            </a>
        )
    }

    return (
        <article data-stream-card className={CARD_CLASS}>
            {body}
        </article>
    )
}

// STREAM: three YouTube items side by side (live first, then upcoming, then the newest archives).
// Cards float up from below; phones swipe through them sideways.
const StreamSection: FC<StreamSectionProps> = ({ streams }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const items = streams.slice(0, 3)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add({ desktop: MOTION_QUERIES.desktop, mobile: MOTION_QUERIES.mobile }, context => {
            const isDesktop = Boolean(context.conditions?.desktop)

            gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 72%' } })
                .from(q('[data-heading-meta]'), { autoAlpha: 0, x: -16, duration: 1, stagger: 0.12, ease: 'power2.out' })
                .from(q('[data-char]'), { autoAlpha: 0, y: 40, filter: isDesktop ? 'blur(8px)' : 'blur(0px)', duration: 1.4, stagger: 0.08, ease: 'power3.out' }, 0)
                .from(q('[data-stream-lead]'), { autoAlpha: 0, y: 20, duration: 1.2, ease: 'power2.out' }, 0.4)

            const cards = q('[data-stream-card]')

            if (cards.length) {
                gsap.from(cards, {
                    autoAlpha: 0,
                    y: isDesktop ? 150 : 70,
                    scale: 0.97,
                    duration: 1.8,
                    stagger: 0.18,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: cards[0], start: 'top 88%' }
                })
            }

            gsap.fromTo(q('[data-stream-outline]'), { xPercent: 4 }, {
                xPercent: -18,
                ease: 'none',
                scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: 1 }
            })

            gsap.fromTo(q('[data-stream-plant]'), { xPercent: 45, rotate: -18, autoAlpha: 0 }, {
                xPercent: 0,
                rotate: -8,
                autoAlpha: 1,
                ease: 'power2.out',
                scrollTrigger: { trigger: section, start: 'top 80%', end: 'top 10%', scrub: 1 }
            })
        })
    }, { scope: sectionRef, dependencies: [items.length] })

    return (
        <section
            id='stream'
            ref={sectionRef}
            className='relative overflow-hidden bg-[linear-gradient(180deg,#eaf2f7_0%,#f1f3ee_55%,#f6f1e7_100%)] py-28 lg:py-40'
        >
            <p
                data-stream-outline
                aria-hidden='true'
                className='pointer-events-none absolute -bottom-[5vw] left-0 whitespace-nowrap font-heading text-[30vw] font-light italic leading-none text-transparent [-webkit-text-stroke:1px_rgba(29,42,51,0.06)] lg:text-[16vw]'
            >
                Stream ✦ Stream ✦
            </p>

            <div data-stream-plant aria-hidden='true' className='pointer-events-none absolute right-[-14vw] top-[24%] w-[38vw] max-w-[380px] lg:right-[-4vw] lg:w-[19vw]'>
                <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: '8.4s' }}>
                    <MahoniaSprig seed={51} className='-scale-x-100' />
                </div>
            </div>

            <div className='relative mx-auto max-w-7xl px-5 lg:px-12'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading index={STREAM_COPY.index} kicker={STREAM_COPY.kicker} title={STREAM_COPY.title} ja={STREAM_COPY.ja} />
                    <p data-stream-lead className='max-w-md text-[15px] leading-[2] tracking-[0.05em] text-ink/80'>
                        {STREAM_COPY.lead}
                    </p>
                </div>

                {items.length > 0
                    ? (
                        <ul
                            aria-label='配信一覧'
                            tabIndex={0}
                            className='no-scrollbar -mx-5 mt-14 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-6 focus-visible:outline-2 focus-visible:outline-gold lg:mx-0 lg:mt-20 lg:grid lg:grid-cols-3 lg:gap-7 lg:overflow-visible lg:px-0 lg:pb-0'
                        >
                            {items.map(stream => (
                                <li key={`${stream.source}-${stream.id}`} className='w-[80vw] max-w-[360px] shrink-0 snap-center lg:w-auto lg:max-w-none'>
                                    <StreamCard stream={stream} />
                                </li>
                            ))}
                        </ul>
                    )
                    : (
                        <div className='mt-14 flex flex-col items-center gap-4 rounded-[22px] border border-dashed border-ink/20 bg-mist/60 px-6 py-16 text-center'>
                            <Sparkle className='size-6 text-gold' />
                            <p className='text-sm tracking-[0.08em] text-ink/75'>{STREAM_COPY.empty}</p>
                        </div>
                    )}

                {siteConfig.links.youtube && (
                    <div className='mt-12 flex justify-center'>
                        <a
                            href={siteConfig.links.youtube}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='inline-flex items-center gap-3 rounded-full px-6 py-3.5 text-sm tracking-[0.14em] ring-1 ring-ink/25 transition-colors duration-500 hover:bg-ink hover:text-mist focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                        >
                            <Youtube className='size-5' />
                            {STREAM_COPY.channel}
                            <ArrowUpRight className='size-4' />
                        </a>
                    </div>
                )}
            </div>
        </section>
    )
}

export default StreamSection
